import { Link } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { Check, ExternalLink, Pencil, Sparkles, Workflow, UserCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';

export const Section = ({ title, description, actions, children, className, testId }) => (
  <section className={cn('rounded-lg border border-border bg-card shadow-card', className)} data-testid={testId}>
    {(title || actions) && <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5">
      <div><h3 className="text-card-title">{title}</h3>{description && <p className="mt-0.5 text-meta text-muted-foreground">{description}</p>}</div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>}
    <div className="p-5">{children}</div>
  </section>
);

export const PageHeader = ({ title, description, actions }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div><h1 className="text-page-title">{title}</h1>{description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}</div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const EmptyState = ({ title, children, actions, icon: Icon, testId = 'empty-state' }) => (
  <div className="flex flex-col items-start gap-2 px-6 py-10" data-testid={testId}>
    {Icon && <span className="mb-1 rounded-lg border border-border bg-muted p-2 text-muted-foreground"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>}
    <p className="text-card-title">{title}</p>
    {children && <p className="max-w-md text-sm text-muted-foreground">{children}</p>}
    {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Timeline = ({ items, testId = 'timeline' }) => (
  <ol className="relative space-y-4 border-l border-border pl-5" data-testid={testId}>
    {items.map((it, i) => (
      <li key={it.id || i} className="relative">
        <span className={cn('absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-card', it.tone === 'ai' ? 'ai-gradient' : it.tone === 'success' ? 'bg-success' : 'bg-primary')} />
        <p className="text-sm font-medium">{it.title}</p>
        {it.body && <p className="text-sm text-muted-foreground">{it.body}</p>}
        {it.meta && <p className="mt-0.5 text-meta text-muted-foreground tabular">{it.meta}</p>}
      </li>))}
  </ol>
);

const AI_STATES = {
  ai_suggested: { label: 'AI suggested', icon: Sparkles, cls: 'ai-glow text-primary bg-card' },
  rule_based: { label: 'Rule-based', icon: Workflow, cls: 'border border-border bg-muted text-muted-foreground' },
  human_reviewed: { label: 'Human reviewed', icon: UserCheck, cls: 'border border-border bg-card text-foreground' },
  approved: { label: 'Approved', icon: Check, cls: 'bg-success/15 text-success' },
};

export const AiChip = ({ state = 'ai_suggested', className, testId }) => {
  const s = AI_STATES[state];
  return <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', s.cls, className)} data-testid={testId || `ai-chip-${state}`}><s.icon className="h-3 w-3" />{s.label}</span>;
};

export const AiIndicator = ({ state, onApprove, onEdit, onReject, sourceHref, disabled, testId = 'ai-indicator' }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
    <AiChip state={state} />
    {onApprove && <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-success" disabled={disabled} onClick={onApprove} data-testid={`${testId}-approve`}><Check className="h-3.5 w-3.5" />Approve</Button>}
    {onEdit && <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" disabled={disabled} onClick={onEdit} data-testid={`${testId}-edit`}><Pencil className="h-3.5 w-3.5" />Edit</Button>}
    {onReject && <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-destructive" disabled={disabled} onClick={onReject} data-testid={`${testId}-reject`}><X className="h-3.5 w-3.5" />Reject</Button>}
    {sourceHref && <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" asChild><Link to={sourceHref} data-testid={`${testId}-source`}><ExternalLink className="h-3.5 w-3.5" />View source</Link></Button>}
  </div>
);

export const Sparkline = ({ data, dataKey = 'v', height = 32 }) => (
  <div style={{ height }} className="w-full"><ResponsiveContainer><LineChart data={data}><Line type="monotone" dataKey={dataKey} stroke="hsl(var(--primary))" strokeWidth={1.75} dot={false} isAnimationActive={false} /></LineChart></ResponsiveContainer></div>
);

export const ProgressBar = ({ value, max, label, testId }) => (
  <div data-testid={testId}>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] ease-ui" style={{ width: `${max ? Math.min(100, (value / max) * 100) : 0}%` }} /></div>
    {label && <p className="mt-1 text-meta text-muted-foreground tabular">{label}</p>}
  </div>
);

export const KpiStat = ({ label, value, hint, sourceHref, sourceLabel = 'View', spark, tone, testId }) => (
  <div className="rounded-lg border border-border bg-card p-4 shadow-card" data-testid={testId}>
    <p className="text-meta font-medium text-muted-foreground">{label}</p>
    <p className={cn('mt-1 text-2xl font-semibold tabular', tone === 'danger' && 'text-destructive', tone === 'success' && 'text-success')}>{value}</p>
    {spark && <div className="mt-2"><Sparkline data={spark} /></div>}
    <div className="mt-2 flex items-center justify-between gap-2">
      {hint && <p className="text-meta text-muted-foreground">{hint}</p>}
      {sourceHref && <Link to={sourceHref} className="ml-auto text-meta font-medium text-primary hover:underline" data-testid={testId && `${testId}-source`}>{sourceLabel}</Link>}
    </div>
  </div>
);

export const Drawer = ({ open, onOpenChange, title, description, children, testId = 'drawer' }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid={testId}>
      <SheetHeader><SheetTitle>{title}</SheetTitle>{description && <SheetDescription>{description}</SheetDescription>}</SheetHeader>
      <div className="mt-4">{children}</div>
    </SheetContent>
  </Sheet>
);
